import { CurrentUser } from '@qualio/ui-components';
import {
  DocumentCategory,
  DocumentFormat,
  DocumentStatus,
  DocumentVersion,
  NextVersionDetails,
  QualioDocument,
  QualioDocumentAttachment,
  QualioDocumentAttachmentPreviewType,
} from '../../../api/model/document';
import {
  hasApprovedPeriodicReview,
  hasEditorFinishedEditing,
  hasPermissions,
  isApprover,
  isApproverWithPendingApproval,
  isBasicUserInAccount,
  isCrossCompanyDocument,
  isEditor,
  isNormalUserInAccount,
  isOwner,
  isQualityEventCollaborator,
  isQualityEventOwner,
  isQualityUserInAccount,
  isReviewer,
} from '../../../util/CurrentUser';

import { Location } from 'react-router';
import {
  ChangeRequestItem,
  ChangeRequestItemNotFound,
} from '../../../api/model/changeRequestItem';
import { Company } from '../../../api/model/companySettings';
import { PeriodicReview } from '../../../api/model/periodicReview';
import { EditorMode } from '../../../util/EditModeUtils';
import { getFileExtension } from '../../../util/FileUploadUtils';
import { getDocumentTagIdsToGroupIdsMap } from './DocumentProperties/utils';
import { Tag } from '../../../api/model/tag';

export const shouldShowFileUpload = (qualioDocument: QualioDocument) => {
  return qualioDocument.document_format === DocumentFormat.DocumentUpload;
};

export const isDraftOwner = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    isOwner(currentUser, qualioDocument) &&
    qualioDocument.status_id === DocumentStatus.Draft
  );
};

export const isDraftEditor = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    isEditor(currentUser, qualioDocument) &&
    !hasEditorFinishedEditing(currentUser, qualioDocument) &&
    qualioDocument.status_id === DocumentStatus.Draft
  );
};

export const isForReviewOwner = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    isOwner(currentUser, qualioDocument) &&
    qualioDocument.status_id === DocumentStatus.For_Review
  );
};

export const isForReviewEditor = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    isEditor(currentUser, qualioDocument) &&
    qualioDocument.status_id === DocumentStatus.For_Review
  );
};

export const isForApprovalOwner = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    isOwner(currentUser, qualioDocument) &&
    qualioDocument.status_id === DocumentStatus.For_Approval
  );
};

export const isApprovalDeclinedOwner = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    isOwner(currentUser, qualioDocument) &&
    qualioDocument.status_id === DocumentStatus.Approval_Declined
  );
};

export const isForReviewReviewer = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    isReviewer(currentUser, qualioDocument) &&
    qualioDocument.status_id === DocumentStatus.For_Review
  );
};

export const isForReviewQualityUser = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    isQualityUserInAccount(currentUser) &&
    qualioDocument.status_id === DocumentStatus.For_Review
  );
};

export const isIssueRelated = (qualioDocument: QualioDocument) => {
  return qualioDocument.category === DocumentCategory.ISSUE_RELATED;
};

export const isPeriodicReviewApprover = (
  currentUser: CurrentUser,
  periodicReview: PeriodicReview,
) => {
  return periodicReview.approvers.some(
    (approver) => approver.id === currentUser.userId,
  );
};

export const getDocumentLatestMajorVersion = (
  versions: DocumentVersion[],
): DocumentVersion | undefined => {
  return versions
    .filter(({ version }) => version.endsWith('.0'))
    .sort((a, b) => Number.parseInt(a.major) - Number.parseInt(b.major))
    .pop();
};

export const isDocumentLatestMajorVersion = (
  qualioDocument: QualioDocument,
  versions: DocumentVersion[],
) => {
  const latestMajorVersion = getDocumentLatestMajorVersion(versions);
  return qualioDocument.id === latestMajorVersion?.document_id;
};

export const isDocumentIssueRelated = (qualioDocument: QualioDocument) =>
  qualioDocument.category === DocumentCategory.ISSUE_RELATED;

export const sortVersions = (versions: DocumentVersion[]) => {
  return [...versions].sort((a, b) => {
    if (Number.parseInt(a.major) === Number.parseInt(b.major)) {
      return Number.parseInt(a.minor) - Number.parseInt(b.minor);
    }
    return Number.parseInt(a.major) - Number.parseInt(b.major);
  });
};

export const getMostRecentVersion = (
  versions: DocumentVersion[],
): DocumentVersion | undefined => {
  const sortedVersions = sortVersions(versions);
  const currentStatusByDocumentId = new Map(
    sortedVersions.map(({ document_id, status_id }) => [
      document_id,
      status_id,
    ]),
  );
  sortedVersions.reverse();
  return sortedVersions.find(
    ({ document_id }) =>
      currentStatusByDocumentId.get(document_id) !== DocumentStatus.Deleted,
  );
};

export const getVersionsToDisplayOrCompare = (
  selectedVersion: string | null | undefined,
  latestMajorVersion: string,
  versions: DocumentVersion[],
) => {
  return versions.filter((ver) => {
    const isFirstMinorOfMajorVersion = ver.minor === '0';

    if (selectedVersion) {
      return (
        (isFirstMinorOfMajorVersion || ver.major === latestMajorVersion) &&
        selectedVersion !== ver.version
      );
    }
    return isFirstMinorOfMajorVersion || ver.major === latestMajorVersion;
  });
};

export const getMostRecentDocumentVersion = (
  versions: DocumentVersion[],
): DocumentVersion | undefined => {
  const mostRecentVersion = sortVersions(versions).pop();
  return mostRecentVersion;
};

export const isMostRecentDocumentVersion = (
  qualioDocument: QualioDocument,
  versions: DocumentVersion[],
) => {
  const mostRecentVersion = getMostRecentDocumentVersion(versions);
  const currentDocumentVersion = `${qualioDocument.major_version}.${qualioDocument.minor_version}`;
  return currentDocumentVersion === mostRecentVersion?.version;
};

export const canUserEditDocument = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    isDraftOwner(qualioDocument, currentUser) ||
    isDraftEditor(qualioDocument, currentUser)
  );
};

export const shouldRenderEditButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  currentEditorMode: EditorMode,
) => {
  return (
    canUserEditDocument(qualioDocument, currentUser) &&
    currentEditorMode === EditorMode.VIEW
  );
};

export const canUserSuggestOnDocument = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    isDraftOwner(qualioDocument, currentUser) ||
    isDraftEditor(qualioDocument, currentUser) ||
    isForReviewOwner(qualioDocument, currentUser) ||
    isForReviewEditor(qualioDocument, currentUser) ||
    isForReviewReviewer(qualioDocument, currentUser) ||
    isForReviewQualityUser(qualioDocument, currentUser)
  );
};

export const shouldRenderSuggestButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  currentEditorMode: EditorMode,
) => {
  return (
    qualioDocument.is_most_recent_version &&
    currentEditorMode === EditorMode.VIEW &&
    canUserSuggestOnDocument(qualioDocument, currentUser)
  );
};

export const shouldRenderEditSuggestToggle = (
  qualioDocument: QualioDocument,
  currentEditorMode: EditorMode,
) => {
  return (
    currentEditorMode !== EditorMode.VIEW &&
    qualioDocument.status_id === DocumentStatus.Draft
  );
};

export const shouldRenderMakeEffectiveButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    hasPermissions(currentUser, ['can_make_doc_effective']) &&
    qualioDocument.status_id === DocumentStatus.Approved
  );
};

export const shouldShowApprovalControls = (
  currentUser: CurrentUser,
  qualioDocument: QualioDocument,
  periodicReview: PeriodicReview | undefined,
  versions: DocumentVersion[] | undefined,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  const isForApproval =
    qualioDocument.status_id === DocumentStatus.For_Approval;
  const isEffective = qualioDocument.status_id === DocumentStatus.Effective;

  if (isForApproval && versions) {
    return (
      isApproverWithPendingApproval(currentUser, qualioDocument) &&
      isMostRecentDocumentVersion(qualioDocument, versions)
    );
  }

  if (isEffective && !!periodicReview) {
    return (
      !periodicReview.round_stopped &&
      periodicReview.approvers
        .map(({ id }) => id)
        .includes(currentUser.userId) &&
      !hasApprovedPeriodicReview(currentUser, periodicReview)
    );
  }

  return false;
};

export const shouldRenderDeleteButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  isMostRecentVersionRequired = false,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  if (isMostRecentVersionRequired && !qualioDocument.is_most_recent_version) {
    return false;
  }
  const deleteAllowedStatuses = [
    DocumentStatus.Draft,
    DocumentStatus.For_Approval,
    DocumentStatus.For_Review,
    DocumentStatus.Approval_Declined,
  ];

  return (
    isOwner(currentUser, qualioDocument) &&
    deleteAllowedStatuses.includes(qualioDocument.status_id as DocumentStatus)
  );
};

export const shouldRenderCreateNewDraftButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  const newDraftAllowedStatuses = [
    DocumentStatus.Approved,
    DocumentStatus.Effective,
    DocumentStatus.Retired,
  ];
  return (
    !isIssueRelated(qualioDocument) &&
    currentUser.permissions.can_author_doc &&
    newDraftAllowedStatuses.includes(qualioDocument.status_id as DocumentStatus)
  );
};

export const shouldRenderSendForReviewButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  currentEditorMode: EditorMode,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    qualioDocument.is_most_recent_version &&
    currentEditorMode === EditorMode.VIEW &&
    isDraftOwner(qualioDocument, currentUser)
  );
};

export const shouldRenderSendForApprovalButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  currentEditorMode: EditorMode,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    qualioDocument.is_most_recent_version &&
    currentEditorMode === EditorMode.VIEW &&
    (isDraftOwner(qualioDocument, currentUser) ||
      isForReviewOwner(qualioDocument, currentUser))
  );
};

export const shouldRenderRevertToDraftButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  currentEditorMode: EditorMode,
  isMostRecentVersionRequired = false,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  if (isMostRecentVersionRequired && !qualioDocument.is_most_recent_version) {
    return false;
  }
  return (
    currentEditorMode === EditorMode.VIEW &&
    (isForReviewOwner(qualioDocument, currentUser) ||
      isForApprovalOwner(qualioDocument, currentUser) ||
      isApprovalDeclinedOwner(qualioDocument, currentUser))
  );
};

export const shouldRenderHistory = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }

  const hasDocumentRole =
    isOwner(currentUser, qualioDocument) ||
    isReviewer(currentUser, qualioDocument) ||
    isApprover(currentUser, qualioDocument);

  return (
    isQualityUserInAccount(currentUser) ||
    isEditor(currentUser, qualioDocument) ||
    (isNormalUserInAccount(currentUser) && hasDocumentRole)
  );
};

export const shouldRenderMarkAsReviewedButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return isForReviewReviewer(qualioDocument, currentUser);
};

export const shouldRenderMarkAsEditedButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    isEditor(currentUser, qualioDocument) &&
    qualioDocument.status_id === DocumentStatus.Draft
  );
};

export const shouldRenderReviewButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return isForReviewReviewer(qualioDocument, currentUser);
};

export const isApprovedOrEffective = (statusId: string) => {
  return [
    DocumentStatus.Approved.valueOf(),
    DocumentStatus.Effective.valueOf(),
  ].includes(statusId);
};

export const shouldShowManageTraineesButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  versions: DocumentVersion[] = [],
) => {
  const { status_id: statusId, training_available: isTrainingAvailable } =
    qualioDocument;
  const docIsApprovedOrEffective = isApprovedOrEffective(statusId);
  const canManageTrainees = 'can_manage_trainees' in currentUser.permissions;
  const isIssueRelated = isDocumentIssueRelated(qualioDocument);
  const isDocOwner = isOwner(currentUser, qualioDocument);
  const isNormalUser = isNormalUserInAccount(currentUser);
  const isLatestMajorVersion = isDocumentLatestMajorVersion(
    qualioDocument,
    versions,
  );
  const isEffectiveAndNotLatest =
    docIsApprovedOrEffective && !isLatestMajorVersion;

  const canManageOnMajorVersion =
    (canManageTrainees || (isDocOwner && isNormalUser)) &&
    docIsApprovedOrEffective &&
    isTrainingAvailable &&
    (isLatestMajorVersion || isEffectiveAndNotLatest) &&
    !isIssueRelated;

  const canManageOnDraft =
    isDocOwner &&
    isTrainingAvailable &&
    statusId === DocumentStatus.Draft.valueOf();

  return canManageOnDraft || canManageOnMajorVersion;
};

const OPEN_DOCUMENT_STATUSES: string[] = [
  DocumentStatus.Draft,
  DocumentStatus.For_Review,
  DocumentStatus.For_Approval,
  DocumentStatus.Approval_Declined,
];

const UNDO_DELETE_BLOCKING_STATUSES: string[] = [
  ...OPEN_DOCUMENT_STATUSES,
  DocumentStatus.Approved,
  DocumentStatus.Effective,
];

export const isUndoDeleteBlockedByAnotherVersion = (
  qualioDocument: QualioDocument,
  versions: DocumentVersion[],
) => {
  const sortedVersions = sortVersions(versions);
  const currentStatusByDocumentId = new Map(
    sortedVersions.map(({ document_id, status_id }) => [
      document_id,
      status_id,
    ]),
  );
  sortedVersions.reverse();
  const newestOtherVersion = sortedVersions.find(
    ({ document_id }) =>
      document_id !== qualioDocument.id &&
      UNDO_DELETE_BLOCKING_STATUSES.includes(
        currentStatusByDocumentId.get(document_id) ?? '',
      ),
  );
  if (!newestOtherVersion) {
    return false;
  }

  const isNewestOtherVersionOpen = OPEN_DOCUMENT_STATUSES.includes(
    currentStatusByDocumentId.get(newestOtherVersion.document_id) ?? '',
  );
  return (
    isNewestOtherVersionOpen ||
    Number.parseInt(newestOtherVersion.major) > qualioDocument.major_version
  );
};

export const shouldRenderUndoDeleteButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  versions: DocumentVersion[] = [],
  isOtherVersionCheckRequired = false,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  if (
    isOtherVersionCheckRequired &&
    isUndoDeleteBlockedByAnotherVersion(qualioDocument, versions)
  ) {
    return false;
  }
  return (
    qualioDocument.status_id === DocumentStatus.Deleted &&
    (isQualityUserInAccount(currentUser) ||
      isOwner(currentUser, qualioDocument))
  );
};

export const shouldRenderRetireButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    qualioDocument.status_id === 'effective' &&
    !qualioDocument.issue_id &&
    currentUser.permissions.can_retire_doc
  );
};

export const shouldRenderRelatedIssues = (qualioDocument: QualioDocument) => {
  return isIssueRelated(qualioDocument) && qualioDocument.issue_id; // issue_id check is needed for deleted drafts
};

export const shouldShowManageTagsButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    (isOwner(currentUser, qualioDocument) ||
      isQualityUserInAccount(currentUser)) &&
    !isIssueRelated(qualioDocument)
  );
};

export const shouldShowAccountAccess = (
  qualioDocument: QualioDocument,
  company: Company | undefined,
  currentUser: CurrentUser,
): boolean =>
  !isIssueRelated(qualioDocument) &&
  Boolean(company?.is_parent) &&
  isQualityUserInAccount(currentUser);

export const canManageAccountAccess = (
  qualioDocument: QualioDocument,
  versions: DocumentVersion[],
  tags: Tag[] = [],
): boolean => {
  const hasPrivateTags =
    getDocumentTagIdsToGroupIdsMap(qualioDocument, tags).size > 0;
  const isMostRecentVersion = qualioDocument.is_most_recent_version;

  return (
    !hasPrivateTags &&
    (isMostRecentVersion ||
      (isApprovedOrEffective(qualioDocument.status_id) &&
        isDocumentLatestMajorVersion(qualioDocument, versions)))
  );
};

export const shouldRenderExportButton = (location: Location) => {
  return !location.pathname.includes('/compare/');
};

export const shouldShowEditPropertiesButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    isDraftOwner(qualioDocument, currentUser) && !isIssueRelated(qualioDocument)
  );
};

export const shouldRenderTrainingTab = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  if (isIssueRelated(qualioDocument) || !qualioDocument.training_available) {
    return false;
  }

  return (
    isOwner(currentUser, qualioDocument) ||
    (isReviewer(currentUser, qualioDocument) &&
      qualioDocument.status_id === DocumentStatus.For_Review) ||
    (isApprover(currentUser, qualioDocument) &&
      qualioDocument.status_id === DocumentStatus.For_Approval)
  );
};

export const shouldRenderCCTab = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return (
    !isIssueRelated(qualioDocument) &&
    (isBasicUserInAccount(currentUser)
      ? isForReviewReviewer(qualioDocument, currentUser)
      : true)
  );
};

export const shouldShowChangeOwnerButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  isEventCollaboratorsEnabled: boolean,
  isMostRecentVersionRequired = false,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  if (isMostRecentVersionRequired && !qualioDocument.is_most_recent_version) {
    return false;
  }
  const hasChangeOwnerPermission =
    hasPermissions(currentUser, ['can_change_doc_owner']) ||
    (isEventCollaboratorsEnabled &&
      isIssueRelated(qualioDocument) &&
      (isQualityEventOwner(currentUser, qualioDocument) ||
        isQualityEventCollaborator(currentUser, qualioDocument)));
  const doesDocStatusAllowChangeOwner = [
    DocumentStatus.Draft.valueOf(),
    DocumentStatus.For_Review.valueOf(),
    DocumentStatus.For_Approval.valueOf(),
    DocumentStatus.Approved.valueOf(),
    DocumentStatus.Effective.valueOf(),
    DocumentStatus.For_Periodic_Review.valueOf(),
    DocumentStatus.Approval_Declined.valueOf(),
    DocumentStatus.Superseded.valueOf(),
  ].includes(qualioDocument.status_id);
  return hasChangeOwnerPermission && doesDocStatusAllowChangeOwner;
};

export const shouldRenderResetEditSessionButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  const doesDocStatusAllowResetSession = [
    DocumentStatus.Draft.valueOf(),
    DocumentStatus.For_Review.valueOf(),
    DocumentStatus.For_Approval.valueOf(),
  ].includes(qualioDocument.status_id);
  const isStaff = hasPermissions(currentUser, ['can_manage_app']);
  return (
    doesDocStatusAllowResetSession &&
    isStaff &&
    qualioDocument.document_format === DocumentFormat.QualioNativeDocument
  );
};

export const shouldRenderRegeneratePreviewButton = (
  qualioDocument: QualioDocument,
  qualioAttachment: QualioDocumentAttachment,
  currentUser: CurrentUser,
) => {
  const isStaff = hasPermissions(currentUser, ['can_manage_app']);
  const isDocumentFormatFileUpload =
    qualioDocument.document_format === DocumentFormat.DocumentUpload;
  const isDocumentTypeAttachment =
    qualioAttachment.preview_type ===
    QualioDocumentAttachmentPreviewType.DOCUMENT;
  const isDocumentTypePdf =
    getFileExtension(qualioAttachment.filename) === 'pdf';

  return (
    isStaff &&
    isDocumentFormatFileUpload &&
    isDocumentTypeAttachment &&
    !isDocumentTypePdf
  );
};

export const shouldRenderChangeFormatButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }

  return (
    isDraftOwner(qualioDocument, currentUser) && !isIssueRelated(qualioDocument)
  );
};

export const shouldRenderChangeOrientationButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  currentEditorMode: EditorMode,
  isMultiRoot?: boolean,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  const canUserEdit = canUserEditDocument(qualioDocument, currentUser);
  const isEditMode = isMultiRoot
    ? canUserEdit
    : currentEditorMode === EditorMode.EDIT;
  return (
    qualioDocument.document_format === DocumentFormat.QualioNativeDocument &&
    isDraftOwner(qualioDocument, currentUser) &&
    !isIssueRelated(qualioDocument) &&
    isEditMode
  );
};

export const shouldRenderApplyTemplateChangesButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }

  return (
    isDraftOwner(qualioDocument, currentUser) && !isIssueRelated(qualioDocument)
  );
};

export const shouldRenderManageApproversButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  const isAllowedForStatusAsOwner = [
    DocumentStatus.For_Approval.valueOf(),
    DocumentStatus.Draft.valueOf(),
    DocumentStatus.For_Review.valueOf(),
  ].includes(qualioDocument.status_id);

  const isAllowedForStatusAsQuality = [
    DocumentStatus.For_Approval.valueOf(),
    DocumentStatus.For_Review.valueOf(),
  ].includes(qualioDocument.status_id);
  const isDocumentOwner = isOwner(currentUser, qualioDocument);
  const isQuality = isQualityUserInAccount(currentUser);

  return (
    (isAllowedForStatusAsOwner && isDocumentOwner) ||
    (isAllowedForStatusAsQuality && isQuality)
  );
};

const getDifferenceInVersions = (
  versionA: DocumentVersion,
  versionB: DocumentVersion,
) => {
  if (Number.parseInt(versionA.major) === Number.parseInt(versionB.major)) {
    return Number.parseInt(versionA.minor) - Number.parseInt(versionB.minor);
  }
  return Number.parseInt(versionA.major) - Number.parseInt(versionB.major);
};

export const versionIsGreaterThan = (
  versionA: DocumentVersion,
  versionB: DocumentVersion,
) => getDifferenceInVersions(versionA, versionB) > 0;

export const shouldRenderManageReviewersButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  if (isOwner(currentUser, qualioDocument)) {
    return [DocumentStatus.Draft, DocumentStatus.For_Review].includes(
      qualioDocument.status_id as DocumentStatus,
    );
  }
  if (isQualityUserInAccount(currentUser)) {
    return qualioDocument.status_id === DocumentStatus.For_Review;
  }
  return false;
};

export const shouldRenderChangeRequest = (
  currentUser: CurrentUser,
  changeRequest: ChangeRequestItem | ChangeRequestItemNotFound | undefined,
  hasFlag: boolean,
) => {
  return (
    (isQualityUserInAccount(currentUser) ||
      isNormalUserInAccount(currentUser)) &&
    changeRequest &&
    (changeRequest.status === 'in_progress' || hasFlag)
  );
};

export const shouldRenderPeriodicReview = (
  currentUser: CurrentUser,
  qualioDocument: QualioDocument,
  periodicReview?: PeriodicReview,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    periodicReview &&
    (isQualityUserInAccount(currentUser) ||
      isOwner(currentUser, qualioDocument) ||
      isPeriodicReviewApprover(currentUser, periodicReview))
  );
};

export const shouldRenderPeriodicReviewButton = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  currentEditorMode: EditorMode,
  nextVersion?: NextVersionDetails,
) => {
  if (isCrossCompanyDocument(currentUser, qualioDocument)) {
    return false;
  }
  return (
    !isIssueRelated(qualioDocument) &&
    isOwner(currentUser, qualioDocument) &&
    qualioDocument.status_id === DocumentStatus.Effective &&
    currentEditorMode === EditorMode.VIEW &&
    !nextVersion
  );
};

export const getTrainee = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  return qualioDocument.trainees.find((trainee) => {
    return trainee.id === currentUser.userId;
  });
};

export const shouldRenderVersionSelector = (
  currentUser: CurrentUser,
  showDocumentVersions?: boolean,
) => {
  return !!showDocumentVersions || !isBasicUserInAccount(currentUser);
};

export const isVersionRestrictedForUser = (
  currentUser: CurrentUser,
  version: DocumentVersion,
) =>
  isBasicUserInAccount(currentUser) &&
  version.status_id !== DocumentStatus.Effective;

export const shouldRenderCompareAgainst = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  latestVersion: DocumentVersion | null,
) => {
  if (
    !latestVersion ||
    qualioDocument.document_format === DocumentFormat.DocumentUpload
  ) {
    return false;
  }

  if (
    isQualityUserInAccount(currentUser) ||
    isNormalUserInAccount(currentUser) ||
    (isBasicUserInAccount(currentUser) &&
      isForReviewReviewer(qualioDocument, currentUser))
  ) {
    return true;
  }

  if (shouldCompleteTraining(qualioDocument, currentUser)) {
    return true;
  }
};

export const shouldCompleteTraining = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
) => {
  const trainee = getTrainee(qualioDocument, currentUser);

  if (!trainee) {
    return false;
  }

  return !trainee.action_done && !trainee.training_removed;
};

export const shouldRenderCompleteTraining = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  currentEditorMode: EditorMode,
) => {
  return (
    currentEditorMode === EditorMode.VIEW &&
    (qualioDocument.status_id === DocumentStatus.Effective ||
      qualioDocument.status_id === DocumentStatus.Approved) &&
    shouldCompleteTraining(qualioDocument, currentUser)
  );
};

export const documentIsInApprovalStatus = (documentStatus: string) => {
  return (
    [DocumentStatus.For_Approval, DocumentStatus.Approval_Declined] as string[]
  ).includes(documentStatus);
};

export const documentIsInTrainingStatus = (
  documentStatus: string,
  trainingAvailable: boolean,
) => {
  return (
    (
      [
        DocumentStatus.Approved,
        DocumentStatus.Effective,
        DocumentStatus.Retired,
        DocumentStatus.Deleted,
      ] as string[]
    ).includes(documentStatus) && trainingAvailable
  );
};

export const shouldRenderActivityReportItem = (
  qualioDocument: QualioDocument,
  currentUser: CurrentUser,
  currentEditorMode: EditorMode,
) => {
  if (currentEditorMode !== EditorMode.VIEW) {
    return false;
  }

  const isDocumentOwner = isOwner(currentUser, qualioDocument);
  const canViewReport = hasPermissions(currentUser, ['can_view_report']);

  const canViewTrainingReport =
    canViewReport &&
    documentIsInTrainingStatus(
      qualioDocument.status_id,
      qualioDocument.training_available,
    );

  const canViewApprovalReport =
    (isDocumentOwner || canViewReport) &&
    documentIsInApprovalStatus(qualioDocument.status_id);

  const canViewReviewReport =
    (isDocumentOwner || canViewReport) &&
    qualioDocument.status_id === DocumentStatus.For_Review;

  return canViewTrainingReport || canViewApprovalReport || canViewReviewReport;
};

export const canManageEditors = (
  user: CurrentUser,
  qualioDocument: QualioDocument,
) =>
  isDraftOwner(qualioDocument, user) ||
  (isQualityUserInAccount(user) &&
    qualioDocument.status_id === DocumentStatus.Draft);
